import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import PublicBrowseShell from '../../components/public/PublicBrowseShell';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import { regionAssociationService } from '../../services/regionAssociationService';
import { regionalTeamService } from '../../services/regionalTeamService';
import { getRegionLabel } from '../../utils/tanzaniaRegions';
import EntityFinancialRecordsPanel from '../../components/finance/EntityFinancialRecordsPanel';

const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <Icon name={icon} size={16} className="text-muted-foreground mt-0.5 shrink-0" />
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-foreground font-medium break-words">{value || 'N/A'}</p>
    </div>
  </div>
);

const LeaderCard = ({ title, name, email, phone, photo }) => (
  <div className="flex items-center gap-3 pb-4 border-b border-border last:border-0 last:pb-0">
    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center overflow-hidden shrink-0">
      {photo ? (
        <Image src={photo} alt={name || title} className="w-full h-full object-cover" />
      ) : (
        <Icon name="User" size={20} className="text-muted-foreground" />
      )}
    </div>
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{title}</p>
      <p className="font-medium text-foreground truncate">{name || 'Not assigned'}</p>
      {(email || phone) && (
        <p className="text-xs text-muted-foreground truncate">{[email, phone].filter(Boolean).join(' \u00b7 ')}</p>
      )}
    </div>
  </div>
);

const RegionDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [region, setRegion] = useState(null);
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadRegion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadRegion = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await regionAssociationService.getById(id);
      setRegion(data);
      document.title = `${data?.associationName || 'Region'} - Tanzania Basketball Federation`;

      if (data?.region) {
        setIsLoadingTeams(true);
        try {
          const regionTeams = await regionalTeamService.getByRegion(data.region);
          setTeams(regionTeams || []);
        } catch (teamErr) {
          console.error('Failed to load regional teams:', teamErr);
          setTeams([]);
        } finally {
          setIsLoadingTeams(false);
        }
      } else {
        setTeams([]);
      }
    } catch (err) {
      setError(err.message || 'Failed to load region');
    } finally {
      setIsLoading(false);
    }
  };

  const isActiveStatus = region?.registrationStatus === 'active' || region?.registrationStatus === 'approved';

  return (
    <PublicBrowseShell>
      <Button variant="ghost" size="sm" iconName="ArrowLeft" onClick={() => navigate('/regions')} className="mb-6">
        Back to Regions
      </Button>

      {isLoading ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="Loader2" size={40} className="text-primary mx-auto mb-4 animate-spin" />
          <p className="text-muted-foreground">Loading region...</p>
        </div>
      ) : error ? (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Icon name="AlertTriangle" size={20} />
            <p className="font-medium">{error}</p>
          </div>
        </div>
      ) : !region ? (
        <div className="bg-card rounded-lg border border-border p-12 card-shadow text-center">
          <Icon name="MapPin" size={40} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Region not found.</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-card rounded-lg border border-border card-shadow p-6 flex items-center gap-6 flex-wrap">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl bg-white border border-border flex items-center justify-center overflow-hidden shrink-0 shadow-sm">
              {region?.logoUrl ? (
                <Image
                  src={region.logoUrl}
                  alt={`${region.associationName || 'Region'} logo`}
                  className="w-full h-full object-contain p-1.5"
                />
              ) : (
                <div className="w-full h-full bg-teal-600/10 flex items-center justify-center">
                  <Icon name="MapPin" size={36} className="text-teal-600" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-foreground">{region?.associationName || 'Unnamed Association'}</h1>
              <p className="text-muted-foreground capitalize">{getRegionLabel(region?.region)}</p>
              <div className="flex items-center gap-2 mt-2">
                <span
                  className={`text-xs px-3 py-1 rounded-full capitalize ${
                    isActiveStatus ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {region?.registrationStatus || 'pending'}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-card rounded-lg border border-border card-shadow p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Contact Information</h2>
              <div className="space-y-4">
                <InfoRow icon="Mail" label="Email" value={region?.email} />
                <InfoRow icon="Phone" label="Mobile" value={region?.mobile} />
                <InfoRow icon="MapPin" label="Physical Address" value={region?.physicalAddress} />
                <InfoRow icon="Mailbox" label="Postal Address" value={region?.postalAddress} />
                <InfoRow icon="Hash" label="Registration Number" value={region?.registrationNumber} />
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border card-shadow p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Association Leadership</h2>
              <div className="space-y-4">
                <LeaderCard
                  title="Chairperson"
                  name={region?.chairpersonName}
                  email={region?.chairpersonEmail}
                  phone={region?.chairpersonMobile}
                  photo={region?.chairpersonPhotoUrl}
                />
                <LeaderCard
                  title="Secretary"
                  name={region?.secretaryName}
                  email={region?.secretaryEmail}
                  phone={region?.secretaryMobile}
                  photo={region?.secretaryPhotoUrl}
                />
              </div>
            </div>
          </div>

          <EntityFinancialRecordsPanel
            regionOnly
            region={region?.region}
            title={`${getRegionLabel(region?.region)} Financial Records`}
          />

          <div className="bg-card rounded-lg border border-border card-shadow p-6">
            <div className="flex items-center justify-between gap-3 mb-4">
              <h2 className="text-lg font-semibold text-foreground">Regional Teams</h2>
              {!isLoadingTeams && (
                <span className="text-sm text-muted-foreground">{teams.length} team{teams.length === 1 ? '' : 's'}</span>
              )}
            </div>

            {isLoadingTeams ? (
              <div className="flex items-center gap-2 text-muted-foreground py-6 justify-center">
                <Icon name="Loader2" size={18} className="animate-spin" />
                Loading regional teams...
              </div>
            ) : teams.length === 0 ? (
              <p className="text-muted-foreground text-sm py-4">
                No approved regional teams registered for {getRegionLabel(region?.region)} yet.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {teams.map((team) => (
                  <Link
                    key={team.id}
                    to={`/regional-teams/${team.id}?from=${id}`}
                    className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/40 hover:bg-muted/30 transition-colors"
                  >
                    <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center overflow-hidden shrink-0">
                      {team.logoUrl ? (
                        <Image src={team.logoUrl} alt={team.teamName} className="w-full h-full object-contain p-1" />
                      ) : (
                        <Icon name="Shield" size={20} className="text-muted-foreground" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-foreground truncate">{team.teamName}</p>
                      <p className="text-xs text-muted-foreground capitalize">
                        {team.registrationStatus?.replace(/_/g, ' ') || 'registered'}
                      </p>
                    </div>
                    <Icon name="ChevronRight" size={16} className="text-muted-foreground shrink-0" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </PublicBrowseShell>
  );
};

export default RegionDetailPage;
